import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { CASES } from "@/content/cases";
import { ArrowUpRight } from "lucide-react";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { collectionSchema } from "@/lib/schema";
import { JsonLd } from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";

export const Route = createFileRoute("/case-studies/")({
  head: () => buildSeo({
    title: "SEO Project Examples & Practical Approaches | Freelancer Tamal",
    description: "Website, campaign and keyword-research examples alongside practical SEO approaches, with evidence limitations stated.",
    path: "/case-studies",
    alternates: { bn: "/bn/case-studies" },
    ogEyebrow: "Case Studies",
    image: "/og/case-studies.jpg",
    keywords: "SEO case studies, SEO results, SEO portfolio Bangladesh, ecommerce SEO case study, local SEO results",
  }),
  component: CaseStudiesIndex,
});

function CaseStudiesIndex() {
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Case Studies", path: "/case-studies" }]} />
      <h1 className="sr-only">SEO Case Studies and Results</h1>
      <Section eyebrow="Case studies" title={<>Projects & <span className="text-gradient">approaches.</span></>}>
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          Project materials and illustrative workflows, with evidence limits stated.
          Unsupported traffic, ranking and ROI claims have been withdrawn.
        </p>
      </Section>

      <div className="container-x grid md:grid-cols-2 gap-6 pb-32">
        {CASES.map((c, i) => (
          <Reveal key={c.slug} delay={(i % 2) * 0.1}>
            <Link
              to="/case-studies/$slug"
              params={{ slug: c.slug }}
              className="group block rounded-3xl border border-border bg-card p-10 hover:border-primary/40 hover:shadow-[var(--shadow-glow)] transition-all h-full"
            >
              <div className="flex items-start justify-between">
                <span className="text-6xl">{c.cover}</span>
                <ArrowUpRight size={20} className="text-muted-foreground group-hover:text-primary group-hover:-translate-y-1 group-hover:translate-x-1 transition" />
              </div>
              <div className="mt-10 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                {c.region} · {c.industry}
              </div>
              <h2 className="mt-3 font-display text-3xl leading-tight">{c.headline}</h2>
              <p className="mt-4 text-muted-foreground">{c.summary}</p>
            </Link>
          </Reveal>
        ))}
      </div>
      <JsonLd
        data={[
          collectionSchema({
            name: "SEO Case Studies",
            path: "/case-studies",
            description:
              "Project examples and practical SEO workflows with stated evidence limitations.",
            items: CASES.map((c) => ({ name: c.headline, path: `/case-studies/${c.slug}` })),
          }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Case Studies", path: "/case-studies" }]),
        ]}
      />
    </>
  );
}